import {
  PARKING_TRANSFER_TYPE,
  PARKING_FEATURE_TYPE,
  ERROR_CODES,
} from "@mozioinc/query";
import {
  Container,
  Filter,
  Loading,
  Tabs,
  TabsList,
  TabsTrigger,
  TAB_VARIANT,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@mozioinc/ui";
import { useQuery } from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { defaultWhiteLabelData } from "../../scripts/white-label";
import { ParkingCard, GeneralError, ErrorDialog } from "../components";
import { Layout, SearchOverview, locationSchema } from "../containers";
import { parkingQuery } from "../utils/query";
import { getWhiteLabel } from "../utils/white-label";
import { ReactComponent as CheapestSvg } from "./assets/cheapest.svg";
import { ReactComponent as ClosestSvg } from "./assets/closest.svg";
import { ReactComponent as InformationSvg } from "./assets/information.svg";
import type { TransNamespace } from "../types";
import type {
  ParkingTransferType,
  ParkingFeatureType,
  ParkingSearchResult,
} from "@mozioinc/query";

export const ISO_DATETIME_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2](?:\d:[0-5]){2}\d\.\d+([+-][0-2]\d:[0-5]\d|)/;

export const searchParamsSchema = z.object({
  location: locationSchema,
  departureDateTime: z.string().regex(ISO_DATETIME_REGEX),
  arrivalDateTime: z.string().regex(ISO_DATETIME_REGEX),
  parkingId: z.string().optional(),
});

const LabelWithExplanation = ({
  label,
  explanation,
}: {
  label: string;
  explanation: string;
}) => {
  return (
    <div>
      <div className="hidden md:flex">
        <span className="self-center">{label}</span>
        <Tooltip>
          <TooltipTrigger className="w-0">
            <InformationSvg className="hover:text-primary-300 ml-1 text-2xl text-gray-400" />
          </TooltipTrigger>
          <TooltipContent>{explanation}</TooltipContent>
        </Tooltip>
      </div>
      <div className="md:hidden">
        {label}
        <p className="mt-1 text-xs text-neutral-800">{explanation}</p>
      </div>
    </div>
  );
};

const isFeaturePresent = (
  filterValue: string[],
  parkingFeatures: ParkingSearchResult["features"],
): boolean => {
  if (
    filterValue.length > 0 &&
    !filterValue.some((featureType) =>
      parkingFeatures.some((feature) => feature.type === featureType),
    )
  ) {
    return false;
  }
  return true;
};

const SearchResultPage = () => {
  const { t } = useTranslation<TransNamespace>("parking:search-result");
  const { arrivalDateTime, departureDateTime, location, parkingId } = useSearch(
    {
      from: "/search",
    },
  );
  const navigate = useNavigate({ from: "/search" });
  let refetchInterval = 500;
  const [results, setResults] = useState<ParkingSearchResult[]>();
  const [showSearchExpired, setShowSearchExpired] = useState(false);

  const whiteLabel = getWhiteLabel();

  const initiateSearchQuery = useQuery({
    queryKey: [
      "parkingInitiateSearch",
      location,
      departureDateTime,
      arrivalDateTime,
    ],
    queryFn: () =>
      parkingQuery.initiateSearch({
        location,
        departureDateTime,
        arrivalDateTime,
        partnerRef:
          whiteLabel.partnerRef === defaultWhiteLabelData.config.partnerRef
            ? undefined
            : whiteLabel.partnerRef,
      }),
  });

  const searchResultQuery = useQuery({
    queryKey: [
      "parkingSearchResults",
      initiateSearchQuery.data?.searchId,
      location,
      departureDateTime,
      arrivalDateTime,
    ],
    enabled: !!initiateSearchQuery.data?.searchId,
    queryFn: () =>
      parkingQuery.getSearchResults({
        searchId: initiateSearchQuery.data?.searchId || "",
      }),
    refetchInterval: () => refetchInterval,
  });

  const error = initiateSearchQuery.error || searchResultQuery.error;
  if (error) {
    throw error;
  }

  refetchInterval = searchResultQuery.data?.moreComing ? 500 : 0;

  const pageViewEventPropsRef = useRef({
    location_value: location.value,
    location_name: location.name,
    arrivalDateTime,
    departureDateTime,
  });

  useEffect(() => {
    posthog.capture(
      "Parking: Search Result Page Viewed",
      pageViewEventPropsRef.current,
    );
  }, []);

  useEffect(() => {
    if (
      searchResultQuery.data?.results &&
      searchResultQuery.data?.results?.length > 0
    ) {
      setResults((previousResults) => [
        ...(previousResults ?? []),
        ...searchResultQuery.data.results,
      ]);
    }
  }, [searchResultQuery.data?.results]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (
        searchResultQuery.data?.expiresAt &&
        Date.now() / 1000 >= searchResultQuery.data.expiresAt
      ) {
        setShowSearchExpired(true);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [searchResultQuery.data?.expiresAt]);

  useEffect(() => {
    if (initiateSearchQuery.isLoading) {
      setResults(undefined);
    }
  }, [initiateSearchQuery.isLoading]);

  const [filterValue, setFilterValue] = useState<{
    type: ParkingFeatureType[];
    transfer: ParkingTransferType[];
    cancelation: ParkingFeatureType[];
    security: ParkingFeatureType[];
  }>({
    type: [],
    transfer: [],
    cancelation: [],
    security: [],
  });

  const [sortValue, setSortValue] = useState<"cheapest" | "closest">(
    "cheapest",
  );

  const parkingFeatureTypeArray: string[] = Object.values(PARKING_FEATURE_TYPE);
  const parkingTransferTypeArray: string[] = Object.values(
    PARKING_TRANSFER_TYPE,
  );
  const counts: Record<string, number> = {};
  [...parkingFeatureTypeArray, ...parkingTransferTypeArray].forEach((type) => {
    counts[type] = 0;
  });

  const parkings = results
    ?.filter((parking) => {
      if (
        filterValue.transfer.length > 0 &&
        !filterValue.transfer.includes(parking.airportTransferSummary.type)
      ) {
        return false;
      }

      parking.features.forEach((feature) => {
        counts[feature.type] += 1;
      });

      if (!isFeaturePresent(filterValue.security, parking.features)) {
        return false;
      }

      if (!isFeaturePresent(filterValue.type, parking.features)) {
        return false;
      }

      if (!isFeaturePresent(filterValue.cancelation, parking.features)) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      return sortValue === "cheapest"
        ? a.price.amount - b.price.amount
        : a.airportTransferSummary.distanceMeter -
            b.airportTransferSummary.distanceMeter;
    });

  results?.forEach((parking) => {
    if (!isFeaturePresent(filterValue.type, parking.features)) {
      return;
    }

    counts[parking.airportTransferSummary.type] += 1;
  });

  const finishedPollingSearchResults =
    searchResultQuery.isFetched && !searchResultQuery.data?.moreComing;
  const noResults = results === undefined || results?.length === 0;

  return (
    <Layout className="flex flex-col items-stretch bg-neutral-100">
      {showSearchExpired && (
        <ErrorDialog
          error={ERROR_CODES.SEARCH_EXPIRED}
          onClick={() => {
            window.location.reload();
          }}
          title={t("Prices need to be updated!")}
        />
      )}
      <SearchOverview
        location={location.displayText}
        arrivalDateTime={
          searchResultQuery.data?.arrivalDateTime ?? new Date(arrivalDateTime)
        }
        departureDateTime={
          searchResultQuery.data?.departureDateTime ??
          new Date(departureDateTime)
        }
      />
      <Container className="mt-2 flex w-full flex-1 flex-col md:mt-12">
        {noResults && finishedPollingSearchResults ? (
          <GeneralError
            title={t("Oops, No Parking Spots!")}
            message={t(
              "We couldn't find a parking spot for your chosen location, date, and time. This might be because we're still growing in that area or all spots are currently taken. Why not try a different search or check in again soon? We're always expanding to new places. Thanks for considering us for your parking needs!",
            )}
            className="flex-1"
          />
        ) : (
          <div className="grid gap-2 md:grid-cols-[auto_1fr] md:gap-6">
            <div className="grid self-start md:sticky md:top-[var(--mz-header-height)] md:h-auto md:w-52">
              <Filter
                value={filterValue}
                options={[
                  {
                    label: t("Free Cancelation"),
                    value: PARKING_FEATURE_TYPE.FREE_CANCELLATION,
                    group: "cancelation",
                    quantity: counts.FREE_CANCELLATION,
                  },
                  {
                    label: t("Covered"),
                    value: PARKING_FEATURE_TYPE.COVERED,
                    group: "type",
                    quantity: counts.COVERED,
                  },
                  {
                    label: t("Uncovered"),
                    value: PARKING_FEATURE_TYPE.UNCOVERED,
                    group: "type",
                    quantity: counts.UNCOVERED,
                  },
                  {
                    label: (
                      <LabelWithExplanation
                        label={t("Meet & Greet")}
                        explanation={t(
                          "Just drop your car at the M&G zone and we'll park it for you!",
                        )}
                      />
                    ),
                    value: PARKING_TRANSFER_TYPE.MEET_AND_GREET,
                    group: "transfer",
                    quantity: counts.MEET_AND_GREET,
                  },
                  {
                    label: (
                      <LabelWithExplanation
                        label={t("Free Shuttle")}
                        explanation={t(
                          "Park offsite and enjoy free, frequent shuttles to the airport!",
                        )}
                      />
                    ),
                    value: PARKING_TRANSFER_TYPE.SHUTTLE,
                    group: "transfer",
                    quantity: counts.SHUTTLE,
                  },
                  {
                    label: (
                      <LabelWithExplanation
                        label={t("Walking Distance")}
                        explanation={t(
                          "Just park your car and it's an easy walk to the airport!",
                        )}
                      />
                    ),
                    value: PARKING_TRANSFER_TYPE.WALK,
                    group: "transfer",
                    quantity: counts.WALK,
                  },
                ]}
                groups={[
                  {
                    label: t("Cancelation Policy"),
                    value: "cancelation",
                  },
                  {
                    label: t("Parking Type"),
                    value: "type",
                  },
                  {
                    label: t("Security"),
                    value: "security",
                  },
                  {
                    label: t("Transfers"),
                    value: "transfer",
                  },
                ]}
                onValueChange={setFilterValue}
                onClear={() =>
                  setFilterValue({
                    type: [],
                    transfer: [],
                    cancelation: [],
                    security: [],
                  })
                }
              />
            </div>

            <div className="flex flex-col gap-2 md:gap-5">
              <Tabs
                className="flex items-center gap-3 md:self-end"
                variant={TAB_VARIANT.BOXED}
                value={sortValue}
                onValueChange={(value) =>
                  setSortValue(value as "cheapest" | "closest")
                }
              >
                <span className="hidden text-lg font-bold md:block">
                  {t("Sort by")}
                </span>

                <TabsList className="flex-1 gap-2">
                  <TabsTrigger
                    value="cheapest"
                    className="flex-1 flex-row justify-center px-4 py-2 md:px-2 md:py-1"
                  >
                    <CheapestSvg className="text-lg" />
                    {t("Cheapest")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="closest"
                    className="flex-1 flex-row justify-center px-4 py-2 md:px-2 md:py-1"
                  >
                    <ClosestSvg className="text-lg" />
                    {t("Closest")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {initiateSearchQuery.data &&
                  parkings &&
                  parkings.map((parking) => (
                    <ParkingCard
                      searchId={initiateSearchQuery.data.searchId}
                      providerId={parking.providerId}
                      resultId={parking.resultId}
                      key={parking.id}
                      detailOpen={parking.id === parkingId}
                      location={parking.location}
                      name={parking.name}
                      logoUrl={parking.logoUrl}
                      price={parking.price}
                      rating={parking.rating}
                      imageUrl={parking.imageUrl}
                      basicInstructionsHtml={parking.basicInstructionsHtml}
                      detailedInstructionsHtml={
                        parking.detailedInstructionsHtml
                      }
                      description={parking.description}
                      airportTransferSummary={parking.airportTransferSummary}
                      features={parking.features}
                      onDetailOpenChange={(open) => {
                        if (open) {
                          navigate({
                            search: (previous) => ({
                              ...previous,
                              parkingId: parking.id,
                            }),
                          });
                        } else {
                          navigate({
                            search: (previous) => {
                              const { parkingId, ...params } = previous;
                              return params;
                            },
                          });
                        }
                      }}
                    />
                  ))}
              </div>
              {(searchResultQuery.isFetching ||
                searchResultQuery.data?.moreComing) && (
                <Loading className="h-full text-3xl" />
              )}
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SearchResultPage;
